import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Button } from '@opendate-app/setlist';
import DonationPaymentStep from '../../../shared/DonationPaymentStep';
import PaymentTerms from '../../../shared/PaymentTerms';
import useSeriesPassCheckoutState from '../useSeriesPassCheckoutState';
import ProductPrice from './ProductPrice';
import PriceSummary from './PriceSummary';
import PassQuestions from './PassQuestions';
import GiftCardEntry from './GiftCardEntry';
import { seriesPassProductShape } from '../productShape';

const PHASE_STATUS = {
  completing: 'Confirming your purchase…',
  thankYou: 'Your purchase is confirmed.',
};

export default function SeriesPassCheckout({
  products,
  seriesPassesCreateUrl,
  stripePublishableKey,
  stripeConnectedAccountId,
  baseCurrencySymbol,
  venueName,
  venueAgreements,
  marketingOptinText = null,
  enableAllInPricing,
  showFeeBreakdown,
  giftCardsAccepted,
  turnstileSiteKey,
}) {
  const {
    selectedProductId,
    setSelectedProductId,
    selectedProduct,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    email,
    setEmail,
    confirmEmail,
    setConfirmEmail,
    contactZip,
    setContactZip,
    quantity,
    setQuantity,
    maxQuantity,
    checkoutQuestions,
    answersForPass,
    setPassAnswer,
    subscribeToVenue,
    setSubscribeToVenue,
    acceptedAgreements,
    setAcceptedAgreements,
    emailsMatch,
    phase,
    clientSecret,
    orderNumber,
    submitting,
    error,
    detailsComplete,
    submitPurchase,
    onPaymentSuccess,
    reset,
    giftCardCheckout,
    paymentFormVersion,
    applyGiftCard,
    removeGiftCard,
    finishCheckout,
    completionError,
    mustRestart,
    giftCardPending,
    cardSubmitting,
    setCardSubmitting,
    fullyCoveredByGiftCards,
  } = useSeriesPassCheckoutState({ products, seriesPassesCreateUrl, venueAgreements });

  const statusText = PHASE_STATUS[phase] || '';
  const withLiveStatus = (content) => (
    <>
      {content}
      <p className="sr-only" role="status">{statusText}</p>
    </>
  );

  const appliedGiftCards = (giftCardCheckout && giftCardCheckout.applied_gift_card_summaries) || [];

  const emailMismatch = confirmEmail.trim().length > 0 && !emailsMatch;

  // The order summary belongs in the page's right sidebar next to Event Info, not
  // wedged between the product picker and the buyer fields. The sidebar lives
  // outside this component's tree, so portal into the slot the page renders.
  // Falls back to rendering inline when the slot is absent (tests, or the widget
  // embedded somewhere without that layout).
  const [summarySlot, setSummarySlot] = useState(null);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    setSummarySlot(document.getElementById('series-pass-summary-slot'));
  }, []);

  const renderSummary = () => {
    const summary = (
      // Live + atomic so switching pass or quantity announces the new total with
      // its labels before the buyer reaches the card step.
      <div aria-live="polite" aria-atomic="true" className="series-pass-summary-card">
        <div className="series-pass-summary-heading">Your order</div>
        <PriceSummary
          product={selectedProduct}
          currency={baseCurrencySymbol}
          quantity={quantity}
          appliedGiftCards={appliedGiftCards}
          amountDue={giftCardCheckout ? giftCardCheckout.gift_card_adjusted_total : null}
        />
      </div>
    );
    if (!selectedProduct) return null;
    return summarySlot ? ReactDOM.createPortal(summary, summarySlot) : summary;
  };

  // Quantity sits ON the product row, next to its price, and only once that
  // product is chosen — mirroring the ticket checkout, where each row carries its
  // own selector. Defaults to 1. stopPropagation because the multi-product row is
  // a <label>: without it, opening the dropdown re-triggers the radio.
  const quantityPicker = (
    <span
      className="series-pass-quantity"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <label className="series-pass-quantity-label" htmlFor="series-pass-quantity">Qty</label>
      <select
        id="series-pass-quantity"
        value={quantity}
        onChange={(e) => setQuantity(Number(e.target.value))}
        data-testid="series-pass-quantity"
      >
        {Array.from({ length: maxQuantity }, (_, index) => index + 1).map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    </span>
  );

  if (phase === 'thankYou') {
    return withLiveStatus(
      <div className="series-pass-checkout" data-testid="series-pass-thankyou">
        <div className="series-pass-thankyou-icon">&#10003;</div>
        <h3 className="series-pass-thankyou-title">You&rsquo;re in!</h3>
        <p className="series-pass-thankyou-message">
          Your {selectedProduct ? selectedProduct.name : 'pass'} purchase is confirmed.
        </p>
        <p className="series-pass-thankyou-message">
          A confirmation email is on its way{email ? ` to ${email}` : ''}.
        </p>
        {orderNumber && (
          <p className="series-pass-thankyou-order" data-testid="series-pass-order-number">
            Order #{orderNumber}
          </p>
        )}
      </div>
    );
  }

  if (phase === 'completing') {
    return withLiveStatus(
      <div className="series-pass-checkout" data-testid="series-pass-completing">
        <p className="series-pass-thankyou-message">Confirming your purchase…</p>
      </div>
    );
  }

  if (phase === 'completionFailed') {
    return withLiveStatus(
      <div className="series-pass-checkout" data-testid="series-pass-completion-failed">
        <p className="series-pass-error" role="alert">{completionError}</p>
        <Button
          variant="primary"
          className="series-pass-cta"
          onClick={mustRestart ? reset : finishCheckout}
          data-testid="series-pass-completion-retry"
        >
          {mustRestart ? 'Start a new order' : 'Try again'}
        </Button>
      </div>
    );
  }

  if (phase === 'payment') {
    return withLiveStatus(
      <div className="series-pass-checkout" data-testid="series-pass-payment">
        <div className="series-pass-section-title" role="heading" aria-level={3}>Payment details</div>
        {/* Repeat the itemized total on the step where the card is charged, so the
            amount the buyer authorizes is never a surprise. */}
        {renderSummary()}
        {giftCardsAccepted && (
          <GiftCardEntry
            appliedGiftCards={appliedGiftCards}
            fullyCovered={fullyCoveredByGiftCards}
            entryClosed={!!(giftCardCheckout && giftCardCheckout.gift_card_entry_closed)}
            requiredCardPaymentCents={giftCardCheckout ? giftCardCheckout.gift_card_required_card_payment_cents : null}
            currency={baseCurrencySymbol}
            turnstileSiteKey={turnstileSiteKey}
            onApply={applyGiftCard}
            onRemove={removeGiftCard}
            disabled={cardSubmitting}
          />
        )}
        {fullyCoveredByGiftCards ? (
          <div className="donation-payment-form">
            <PaymentTerms submitLabel="Complete purchase" />
            <Button
              variant="primary"
              className="series-pass-cta"
              disabled={giftCardPending}
              onClick={finishCheckout}
              data-testid="series-pass-gift-card-complete"
            >
              Complete purchase
            </Button>
          </div>
        ) : (
          <DonationPaymentStep
            refreshKey={paymentFormVersion}
            onSubmittingChange={setCardSubmitting}
            submitDisabled={giftCardPending}
            stripePublishableKey={stripePublishableKey}
            stripeConnectedAccountId={stripeConnectedAccountId}
            clientSecret={clientSecret}
            onSuccess={onPaymentSuccess}
            submitLabel="Complete purchase"
            buttonClassName="btn series-pass-buy-btn"
          />
        )}
      </div>
    );
  }

  return withLiveStatus(
    <div className="series-pass-checkout" data-testid="series-pass-details">
      <div className="series-pass-section-title" role="heading" aria-level={3}>Choose your pass</div>
      {products.length > 0 && (
        <div
          className="series-pass-products"
          role="radiogroup"
          aria-label="Choose your pass"
        >
          {products.map((product) => (
            <label
              key={product.id}
              className={`series-pass-product${selectedProductId === product.id ? ' is-selected' : ''}`}
            >
              <input
                type="radio"
                name="series-pass-product"
                value={product.id}
                checked={selectedProductId === product.id}
                onChange={() => setSelectedProductId(product.id)}
              />
              <span className="series-pass-product-info">
                <span className="series-pass-product-name">{product.name}</span>
                {product.description && (
                  <span className="series-pass-product-description">{product.description}</span>
                )}
              </span>
              {selectedProductId === product.id && quantityPicker}
              <ProductPrice
                product={product}
                currency={baseCurrencySymbol}
                enableAllInPricing={enableAllInPricing}
                showFeeBreakdown={showFeeBreakdown}
              />
            </label>
          ))}
        </div>
      )}


      {/* What the buyer will actually be charged, itemized. The live region is
          always mounted (and atomic, so the labels are read with the amounts) so
          picking a different pass announces the new total before the card step. */}
      {renderSummary()}

      <div className="series-pass-fields">
        <input
          type="text"
          placeholder="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          data-testid="series-pass-first-name"
        />
        <input
          type="text"
          placeholder="Last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          data-testid="series-pass-last-name"
        />
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="series-pass-email"
        />
        <input
          type="email"
          placeholder="Confirm email"
          value={confirmEmail}
          onChange={(e) => setConfirmEmail(e.target.value)}
          data-testid="series-pass-confirm-email"
        />
        {emailMismatch && (
          <div className="series-pass-field-error" data-testid="series-pass-email-mismatch">
            Emails don&rsquo;t match.
          </div>
        )}
        <input
          type="text"
          placeholder="Postal code"
          value={contactZip}
          onChange={(e) => setContactZip(e.target.value)}
          data-testid="series-pass-zip"
        />
      </div>

      {checkoutQuestions.length > 0 && (
        <PassQuestions
          quantity={quantity}
          answersForPass={answersForPass}
          onAnswerChange={setPassAnswer}
        />
      )}

      {error && (
        <div className="series-pass-error" role="alert" data-testid="series-pass-error">
          {error}
        </div>
      )}

      <div className="series-pass-consents">
        <label className="series-pass-optin">
          <input
            type="checkbox"
            className="series-pass-optin-input"
            checked={subscribeToVenue}
            onChange={(e) => setSubscribeToVenue(e.target.checked)}
            data-testid="series-pass-optin"
          />
          <span>
            {marketingOptinText
              ? marketingOptinText
              : `Keep me posted about events and news${venueName ? ` from ${venueName}` : ''}.`}
          </span>
        </label>
        {venueAgreements.length > 0 && (
          <div className="series-pass-agreements" data-testid="series-pass-agreements">
            {venueAgreements.map((agreement) => (
              <label key={agreement.id} className="series-pass-agreement">
                <input
                  type="checkbox"
                  className="series-pass-agreement-input"
                  checked={!!acceptedAgreements[agreement.id]}
                  onChange={(e) =>
                    setAcceptedAgreements((current) => ({
                      ...current,
                      [agreement.id]: e.target.checked,
                    }))
                  }
                  data-testid={`series-pass-agreement-${agreement.id}`}
                />
                {/* Agreement body is organizer-authored HTML, sanitized server-side
                    (VenueAgreement#sanitize_body) — same source the regular checkout renders. */}
                <span
                  className="series-pass-agreement-body"
                  dangerouslySetInnerHTML={{ __html: agreement.body }}
                />
              </label>
            ))}
          </div>
        )}
      </div>

      <Button
        variant="primary"
        className="series-pass-cta"
        disabled={!detailsComplete || submitting}
        onClick={submitPurchase}
        data-testid="series-pass-continue"
      >
        {submitting ? 'Processing…' : 'Continue to payment'}
      </Button>
    </div>
  );
}

SeriesPassCheckout.propTypes = {
  products: PropTypes.arrayOf(seriesPassProductShape).isRequired,
  seriesPassesCreateUrl: PropTypes.string.isRequired,
  stripePublishableKey: PropTypes.string.isRequired,
  stripeConnectedAccountId: PropTypes.string,
  baseCurrencySymbol: PropTypes.string,
  venueName: PropTypes.string,
  venueAgreements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      body: PropTypes.string.isRequired,
      required: PropTypes.bool,
    })
  ),
  marketingOptinText: PropTypes.string,
  enableAllInPricing: PropTypes.bool,
  showFeeBreakdown: PropTypes.bool,
  giftCardsAccepted: PropTypes.bool,
  turnstileSiteKey: PropTypes.string,
};

SeriesPassCheckout.defaultProps = {
  stripeConnectedAccountId: null,
  baseCurrencySymbol: 'USD',
  venueName: null,
  venueAgreements: [],
  marketingOptinText: null,
  enableAllInPricing: false,
  showFeeBreakdown: true,
  giftCardsAccepted: false,
  turnstileSiteKey: null,
};
